import { formatPhoneNumber } from "react-phone-number-input"

import useDialogConfirmClose from "@hooks/useDialogConfirmClose"
import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import { Button } from "@atoms/index"

import DialogFooter from "@molecules/Dialog/DialogFooter/DialogFooter"
import DialogHeader from "@molecules/Dialog/DialogHeader/DialogHeader"
import OTPInput from "@molecules/Form/inputs/OTPInput/OTPInput"
import { AlertBanner, Dialog, Prompt } from "@molecules/index"

import OTPForm from "@organisms/OTPForm/OTPForm"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import styles from "./SecurityCheckDialog.module.scss"
import { SecurityCheckDialogProps } from "./SecurityCheckDialog.types"

export default function SecurityCheckDialog(props: SecurityCheckDialogProps) {
    const {
        children = undefined,
        checkFor,
        onSuccess,
        isOpen,
        onOpenChange,
        title = "Security check",
        entryPoint = "verify",
        leftActionOverride,
        onDiscard,
    } = props

    // Only use controlled mode when both isOpen AND onOpenChange are provided
    const isControlledMode = isOpen !== undefined && onOpenChange !== undefined
    const effectiveIsOpen = isControlledMode ? isOpen : undefined

    const { user } = useUser()
    const isDesktop = useIsDesktop()

    const {
        isDialogOpen,
        isPromptOpen,
        handleCloseAttempt,
        handleDialogOpenChange,
        handlePromptCancel,
        handlePromptDiscard,
    } = useDialogConfirmClose({
        isOpen: effectiveIsOpen,
        onOpenChange: isControlledMode ? onOpenChange : undefined,
        onDiscard,
        shouldConfirmClose: () => entryPoint === "change",
    })

    const contactInfo = user
        ? checkFor === "email"
            ? user?.[checkFor]
            : formatPhoneNumber(user?.[checkFor])
        : "\u2014"

    return (
        <>
            <OTPForm.Root
                checkFor={checkFor}
                codeRequestEndpoint={
                    checkFor === "email" ? AUTH_ENDPOINTS.OTP.EMAIL_REQUEST : AUTH_ENDPOINTS.OTP.SMS_REQUEST
                }
                codeVerificationEndpoint={
                    checkFor === "email" ? AUTH_ENDPOINTS.OTP.EMAIL_VERIFY : AUTH_ENDPOINTS.OTP.SMS_VERIFY
                }
                onCodeVerificationSuccessful={onSuccess}
                triggerOTPReset={!isDialogOpen && !isPromptOpen}
                triggerSendCode={user !== null && (isDialogOpen || isPromptOpen)}
            >
                <Dialog.Root
                    mode="dialog"
                    variant="default"
                    isOpen={isDialogOpen}
                    onOpenChange={handleDialogOpenChange}
                >
                    {children && <Dialog.Trigger>{children}</Dialog.Trigger>}
                    <Dialog.Content className={styles.dialogContent}>
                        <DialogHeader.Root>
                            <DialogHeader.Title title={title} />
                            {isDesktop && <DialogHeader.CloseButton onClick={handleCloseAttempt} />}
                        </DialogHeader.Root>
                        <Dialog.Body>
                            <div className={styles.dialogBody}>
                                <span className={styles.subtitle}>
                                    Enter the code we just sent to{" "}
                                    <span className={styles.subtitleHighlight}>{contactInfo}</span>
                                </span>
                                <div className={styles.otpInput}>
                                    <OTPInput isFocused={true} isTabbable={true} />
                                </div>
                                {checkFor === "email" && (
                                    <AlertBanner
                                        message="Check your inbox and spam folders."
                                        status="info"
                                        type="tip"
                                        title="Tip:"
                                    />
                                )}
                            </div>
                            <OTPForm.AlertBanner />
                        </Dialog.Body>
                        <DialogFooter.Root>
                            <DialogFooter.LeftSide>
                                {leftActionOverride ?? (
                                    <Button size="lg" variant="ghost" onClick={handleCloseAttempt}>
                                        Cancel
                                    </Button>
                                )}
                            </DialogFooter.LeftSide>
                            <DialogFooter.RightSide>
                                <OTPForm.SendCodeButton isFullWidth={false} />
                            </DialogFooter.RightSide>
                        </DialogFooter.Root>
                    </Dialog.Content>
                </Dialog.Root>
            </OTPForm.Root>

            {entryPoint === "change" && (
                <Prompt
                    isOpen={isPromptOpen}
                    title="Discard progress?"
                    body="If you discard, your progress will not be saved."
                    onCancel={handlePromptCancel}
                    onConfirm={handlePromptDiscard}
                    cancelText="Cancel"
                    confirmText="Discard"
                    isDangerousOperation={true}
                />
            )}
        </>
    )
}
